/**
 * One couple, everything about them.
 *
 * Presentation only — every mutation lives in WeddingCouplesPage, which owns
 * the toast and the reload. Same split as BookDetailPage and its dialogs.
 *
 * THE UNREDEEMED-CLAIMS PANEL IS THE POINT OF THIS DIALOG.
 * A Stripe Payment Link purchase creates a `wedding_purchase_claims` row and
 * nothing else; it only becomes a `wedding_purchases` row when the buyer
 * redeems it. So money that has been taken but never claimed appears in NO
 * revenue figure on any other screen — not the Overview tile, not the Revenue
 * page, not the book's own total. This is the only place it is visible, and it
 * is exactly the "I paid and nothing happened" support call.
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Box,
  Chip,
  Divider,
  Alert,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
} from '@mui/material';
import { WeddingStatusChip, WeddingTierChip } from '../WeddingChips';
import type { CoupleDetail, CoupleBookRow } from '../weddingApi';
import { centsToUsd, num, shortDate, relativeTime } from '../weddingFormat';

interface Props {
  open: boolean;
  loading: boolean;
  detail: CoupleDetail | null;
  error: string | null;
  onClose: () => void;
  onEdit: () => void;
  onResetPassword: () => void;
  onResendVerification: () => void;
  onUnlock: () => void;
  onChangeTier: (book: CoupleBookRow) => void;
}

const Fact: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="body2">{children}</Typography>
  </Box>
);

const CoupleDetailDialog: React.FC<Props> = ({
  open, loading, detail, error, onClose, onEdit,
  onResetPassword, onResendVerification, onUnlock, onChangeTier,
}) => {
  const couple = detail?.couple;
  const unredeemed = detail?.unredeemed_claims || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {couple ? `${couple.partner1_name} & ${couple.partner2_name}` : 'Couple'}
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && error && <Alert severity="error">{error}</Alert>}

        {!loading && detail && couple && (
          <Stack spacing={2.5}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                label={couple.email_verified ? 'Email verified' : 'Email not verified'}
                color={couple.email_verified ? 'success' : 'warning'}
                variant="outlined"
              />
              {couple.locked_out && (
                <Chip size="small" label="Locked out" color="error" variant="outlined" />
              )}
              {couple.reset_pending && (
                <Chip size="small" label="Reset link outstanding" color="info" variant="outlined" />
              )}
            </Stack>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' },
                gap: 2,
              }}
            >
              <Fact label="Email">
                <Link href={`mailto:${couple.email}`}>{couple.email}</Link>
              </Fact>
              <Fact label="Registered">{shortDate(couple.created_at)}</Fact>
              <Fact label="Last sign-in">
                {couple.last_login_at ? relativeTime(couple.last_login_at) : 'never'}
              </Fact>
              <Fact label="Active sessions">{num(detail.sessions.active_sessions)}</Fact>
              <Fact label="Failed sign-ins">{num(couple.failed_login_attempts)}</Fact>
              <Fact label="Books">{detail.books.length}</Fact>
            </Box>

            {unredeemed.length > 0 && (
              <Alert severity="warning" variant="outlined">
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>
                    {unredeemed.length} paid purchase
                    {unredeemed.length === 1 ? ' has' : 's have'} never been claimed.
                  </strong>
                </Typography>
                <Typography variant="body2">
                  This money is in no revenue figure anywhere else in the
                  dashboard — a Payment Link purchase only becomes a recorded
                  purchase when the buyer redeems it.
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                  {unredeemed.map((c) => (
                    <Chip
                      key={c.id}
                      size="small"
                      label={`${c.tier} · ${centsToUsd(c.amount_cents)} · ${shortDate(c.created_at)}`}
                    />
                  ))}
                </Stack>
              </Alert>
            )}

            <Divider />

            <Box>
              <Typography variant="subtitle2" gutterBottom>Books</Typography>
              {detail.books.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  This couple registered but has no book yet.
                </Typography>
              )}
              {detail.books.length > 0 && (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Code</TableCell>
                      <TableCell>Tier</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Contributors</TableCell>
                      <TableCell align="right">Revenue</TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detail.books.map((b) => {
                      const cap = num(b.max_contributors) + num(b.extra_contributor_count);
                      const live = num(b.live_guests);
                      return (
                        <TableRow key={b.id}>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{b.wedding_code}</TableCell>
                          <TableCell><WeddingTierChip tier={b.tier} /></TableCell>
                          <TableCell><WeddingStatusChip status={b.status} /></TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              color={live > cap ? 'error.main' : 'text.primary'}
                            >
                              {live} / {cap}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{centsToUsd(b.revenue_cents)}</TableCell>
                          <TableCell align="right">
                            <Button size="small" onClick={() => onChangeTier(b)}>
                              Change tier
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </Box>

            {detail.purchases.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>Purchases</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Book</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Tier</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detail.purchases.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{p.wedding_code}</TableCell>
                        <TableCell>{p.purchase_type}</TableCell>
                        <TableCell>{p.tier || '—'}</TableCell>
                        <TableCell align="right">{centsToUsd(p.amount_cents)}</TableCell>
                        <TableCell>{p.status}</TableCell>
                        <TableCell>{shortDate(p.purchased_at || p.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={onEdit} disabled={!detail}>Edit names</Button>
        <Button onClick={onResetPassword} disabled={!detail}>Send password reset</Button>
        {couple && !couple.email_verified && (
          <Button onClick={onResendVerification}>Resend verification</Button>
        )}
        {couple && couple.locked_out && (
          <Button onClick={onUnlock} color="warning">Unlock</Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} variant="contained">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CoupleDetailDialog;
